import React, { useState } from 'react';
import { Calendar, CheckCircle, AlertTriangle, Zap, Database, Cpu, Globe, Target, Clock, TrendingUp, Code, FileText } from 'lucide-react';

type Props = {
  theme: 'light' | 'dark';
};

const SiloettRoadmap: React.FC<Props> = ({ theme }) => {
  const [selectedPhase, setSelectedPhase] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  const isDark = theme === 'dark';
  const pageBg = isDark ? 'bg-app-dark text-white' : 'bg-app-light text-slate-900';
  const cardBg = isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-slate-200';
  const muted = isDark ? 'text-gray-300' : 'text-slate-600';
  const subMuted = isDark ? 'text-gray-200' : 'text-slate-700';

  const phases = [
    {
      id: 'phase1',
      name: 'Phase 1: Foundation',
      weeks: '1-2',
      weekNumbers: [1, 2],
      color: 'from-orange-500 to-orange-600',
      icon: Database,
      goal: 'Build the canon data pipeline and storage infrastructure',
      keyDeliverables: [
        'Canon data model design (entities, relationships, timelines)',
        'PDF ingestion pipeline (extract text, metadata, chunk documents)',
        'Pinecone vector database setup with first 5 test documents',
        'PostgreSQL schema for structured metadata',
        'Basic RAG query working end-to-end'
      ],
      techStack: ['Python', 'PyPDF2', 'Pinecone', 'PostgreSQL', 'OpenAI Embeddings'],
      risks: [
        { risk: 'PDF parsing fails on complex layouts', mitigation: 'Use pdfplumber as fallback, start with simple scripts' },
        { risk: 'Vector DB setup complexity', mitigation: 'Use Pinecone managed service (no DevOps needed)' }
      ],
      successCriteria: 'Can ingest a script, query it, and get a cited response',
      effort: '40-50 hours',
      criticalPath: true
    },
    {
      id: 'phase2',
      name: 'Phase 2: Intelligence Layer',
      weeks: '3-5',
      weekNumbers: [3, 4, 5],
      color: 'from-green-500 to-green-600',
      icon: Zap,
      goal: 'Implement RAG pipeline and validation logic',
      keyDeliverables: [
        'LangChain RAG pipeline with citation extraction',
        'Rule-based validation (timeline conflicts, character death state)',
        'LLM-based validation (character consistency, world rules)',
        'Validation result formatting (severity levels, suggestions)',
        'Script generation with canon grounding'
      ],
      techStack: ['LangChain', 'Claude Sonnet 4 API', 'Custom validation rules', 'Prompt engineering'],
      risks: [
        { risk: 'LLM validation quality inconsistent', mitigation: 'Start with rule-based, add LLM as enhancement' },
        { risk: 'Citation accuracy issues', mitigation: 'Use rag-citation library, test with known examples' }
      ],
      successCriteria: 'Validation catches 3+ issue types, generation creates coherent scenes',
      effort: '60-70 hours',
      criticalPath: true
    },
    {
      id: 'phase3',
      name: 'Phase 3: API Layer',
      weeks: '6-7',
      weekNumbers: [6, 7],
      color: 'from-purple-500 to-purple-600',
      icon: Cpu,
      goal: 'Build backend API and connect all components',
      keyDeliverables: [
        'FastAPI endpoints (query, validate, generate, ingest)',
        'Request/response schemas with proper error handling',
        'API documentation (auto-generated by FastAPI)',
        'Railway deployment configuration',
        'End-to-end integration testing'
      ],
      techStack: ['FastAPI', 'Pydantic', 'Railway', 'Docker', 'API testing tools'],
      risks: [
        { risk: 'API performance bottlenecks', mitigation: 'Add caching for common queries, async endpoints' },
        { risk: 'Deployment issues', mitigation: 'Railway has simple Python templates, test early' }
      ],
      successCriteria: 'All endpoints working, API deployed and accessible via URL',
      effort: '40-50 hours',
      criticalPath: true
    },
    {
      id: 'phase4',
      name: 'Phase 4: User Interface',
      weeks: '8-9',
      weekNumbers: [8, 9],
      color: 'from-blue-500 to-blue-600',
      icon: Globe,
      goal: 'Build demo UI and connect to backend',
      keyDeliverables: [
        'React components for all 3 screens (Search, Validate, Generate)',
        'Frontend-backend integration via REST API',
        'Loading states, error handling, UX polish',
        'Vercel deployment setup',
        'Responsive design testing'
      ],
      techStack: ['v0.dev', 'React', 'Tailwind CSS', 'Vercel', 'Axios/Fetch'],
      risks: [
        { risk: 'UI complexity takes longer than expected', mitigation: 'Use v0.dev to generate 80% of components' },
        { risk: 'API integration bugs', mitigation: 'Test with Postman first, then add frontend' }
      ],
      successCriteria: 'Working web app deployed, all features accessible via UI',
      effort: '50-60 hours',
      criticalPath: false
    },
    {
      id: 'phase5',
      name: 'Phase 5: Demo Preparation',
      weeks: '10-11',
      weekNumbers: [10, 11],
      color: 'from-cyan-500 to-cyan-600',
      icon: Target,
      goal: 'Polish demos and prepare fundraising materials',
      keyDeliverables: [
        'Script 3 demo scenarios with test data',
        'Test each demo 50+ times, fix all edge cases',
        'Record demo video (3-minute walkthrough)',
        'Prepare pitch deck with screenshots',
        'Write technical documentation'
      ],
      techStack: ['Demo scripts', 'Screen recording', 'Pitch deck tools', 'Documentation'],
      risks: [
        { risk: 'Demos fail during live presentation', mitigation: 'Have pre-recorded backup video ready' },
        { risk: 'Edge cases discovered late', mitigation: 'Start testing in Week 9, not Week 10' }
      ],
      successCriteria: '3 demos work 100% reliably, video recorded, deck complete',
      effort: '40-50 hours',
      criticalPath: true
    },
    {
      id: 'phase6',
      name: 'Phase 6: Buffer & Contingency',
      weeks: '12',
      weekNumbers: [12],
      color: 'from-yellow-500 to-yellow-600',
      icon: AlertTriangle,
      goal: 'Address unexpected issues and final polish',
      keyDeliverables: [
        'Fix any critical bugs discovered in Week 11',
        'Performance optimization if needed',
        'Final UI polish and styling tweaks',
        'Backup plan implementation (Wizard of Oz)',
        'Dry run presentations'
      ],
      techStack: ['Debugging tools', 'Performance profiling', 'Contingency planning'],
      risks: [
        { risk: 'Major bug found in Week 11', mitigation: 'This entire week is buffer time' },
        { risk: 'Feature creep temptation', mitigation: 'Strict scope freeze after Week 10' }
      ],
      successCriteria: 'MVP is demo-ready, all stakeholders have tested it',
      effort: '20-40 hours (as needed)',
      criticalPath: false
    }
  ];

  const milestones = [
    { week: 2, title: 'First RAG Query Works', type: 'technical' },
    { week: 5, title: 'Validation Catches Conflicts', type: 'technical' },
    { week: 7, title: 'API Deployed to Railway', type: 'deployment' },
    { week: 9, title: 'Full UI Deployed to Vercel', type: 'deployment' },
    { week: 11, title: 'Demos Ready for Review', type: 'business' },
    { week: 12, title: 'MVP Complete - Ready to Fundraise', type: 'business' }
  ];

  const weeklyBreakdown = [
    { week: 1, focus: 'Data model + PDF parsing', hours: 20, status: 'Foundation' },
    { week: 2, focus: 'Vector DB setup + first queries', hours: 25, status: 'Foundation' },
    { week: 3, focus: 'LangChain RAG pipeline', hours: 20, status: 'Intelligence' },
    { week: 4, focus: 'Validation logic (rules)', hours: 22, status: 'Intelligence' },
    { week: 5, focus: 'LLM validation + generation', hours: 25, status: 'Intelligence' },
    { week: 6, focus: 'FastAPI endpoints', hours: 18, status: 'Backend' },
    { week: 7, focus: 'Deployment + integration tests', hours: 22, status: 'Backend' },
    { week: 8, focus: 'React UI components', hours: 25, status: 'Frontend' },
    { week: 9, focus: 'Frontend-backend connection', hours: 25, status: 'Frontend' },
    { week: 10, focus: 'Demo scripting + testing', hours: 20, status: 'Polish' },
    { week: 11, focus: 'Video recording + documentation', hours: 20, status: 'Polish' },
    { week: 12, focus: 'Buffer + final fixes', hours: 15, status: 'Buffer' }
  ];

  const getMilestoneColor = (type: string) => {
    switch(type) {
      case 'technical': return 'bg-green-500';
      case 'deployment': return 'bg-blue-500';
      case 'business': return 'bg-purple-500';
      default: return 'bg-gray-500';
    }
  };

  const totalHours = weeklyBreakdown.reduce((sum, week) => sum + week.hours, 0);

  return (
    <div className={`min-h-screen ${pageBg} p-8`}>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className={`text-4xl font-bold mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>SILOETT MVP Development Roadmap</h1>
          <p className={`${muted} text-lg mb-6`}>12-Week Build Plan • Solo Founder • AI-Assisted Development</p>
          
          <div className="flex gap-4 justify-center">
            <div className={`${cardBg} px-6 py-3 rounded-lg border`}>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>{totalHours}</div>
              <div className={`text-sm ${muted}`}>Total Hours</div>
            </div>
            <div className={`${cardBg} px-6 py-3 rounded-lg border`}>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>12</div>
              <div className={`text-sm ${muted}`}>Weeks</div>
            </div>
            <div className={`${cardBg} px-6 py-3 rounded-lg border`}>
              <div className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>6</div>
              <div className={`text-sm ${muted}`}>Phases</div>
            </div>
            <div className={`${cardBg} px-6 py-3 rounded-lg border`}>
              <div className="text-2xl font-bold text-green-400">{milestones.length}</div>
              <div className={`text-sm ${muted}`}>Key Milestones</div>
            </div>
          </div>

          <div className="mt-6">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-6 py-2 bg-gradient-to-r from-teal-500 to-orange-500 hover:scale-102 text-white rounded-lg transition-all"
            >
              {showDetails ? 'Hide' : 'Show'} Weekly Breakdown
            </button>
          </div>
        </div>

        {/* Phase Cards */}
        <div className="space-y-6 mb-12">
          {phases.map((phase) => {
            const Icon = phase.icon;
            const isActive = selectedPhase === phase.id;
            
            return (
              <div key={phase.id}>
                <div
                  onClick={() => setSelectedPhase(isActive ? null : phase.id)}
                  className={`
                    cursor-pointer transition-all duration-300 rounded-xl p-6 
                    ${isActive ? 'scale-102 shadow-2xl' : 'hover:scale-101 shadow-lg'}
                    bg-gradient-to-r ${phase.color}
                  `}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-white bg-opacity-20 p-3 rounded-lg">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-bold text-white">{phase.name}</h3>
                        <span className="px-3 py-1 bg-white bg-opacity-20 rounded-full text-sm text-white">
                          Weeks {phase.weeks}
                        </span>
                        {phase.criticalPath && (
                          <span className="px-3 py-1 bg-red-500 bg-opacity-30 border border-red-400 rounded-full text-xs text-red-200 font-semibold">
                            CRITICAL PATH
                          </span>
                        )}
                      </div>
                      <p className="text-white text-opacity-90 text-lg mb-3">{phase.goal}</p>
                      <div className="flex items-center gap-4 text-sm text-white text-opacity-80">
                        <div className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          <span>{phase.effort}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Code className="w-4 h-4" />
                          <span>{phase.techStack.length} Technologies</span>
                        </div>
                      </div>

                      {isActive && (
                        <div className="mt-6 pt-6 border-t border-white border-opacity-20 space-y-6">
                          {/* Key Deliverables */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                              <CheckCircle className="w-5 h-5" />
                              Key Deliverables
                            </h4>
                            <ul className="space-y-2">
                              {phase.keyDeliverables.map((deliverable, idx) => (
                                <li key={idx} className="flex items-start gap-2 text-white text-opacity-90">
                                  <span className="text-white text-opacity-60 mt-1">•</span>
                                  <span>{deliverable}</span>
                                </li>
                              ))}
                            </ul>
                          </div>

                          {/* Tech Stack */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                              <Code className="w-5 h-5" />
                              Technology Stack
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {phase.techStack.map((tech, idx) => (
                                <span key={idx} className="px-3 py-1 bg-white bg-opacity-10 rounded-full text-sm text-white">
                                  {tech}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Risks & Mitigation */}
                          <div>
                            <h4 className="text-lg font-semibold text-white mb-3 flex items-center gap-2">
                              <AlertTriangle className="w-5 h-5" />
                              Risk Mitigation
                            </h4>
                            <div className="space-y-3">
                              {phase.risks.map((item, idx) => (
                                <div key={idx} className="bg-white bg-opacity-10 rounded-lg p-3">
                                  <p className="text-white font-medium mb-1">⚠️ {item.risk}</p>
                                  <p className="text-white text-opacity-80 text-sm">✓ {item.mitigation}</p>
                                </div>
                              ))}
                            </div>
                          </div>

                          {/* Success Criteria */}
                          <div className="bg-green-500 bg-opacity-20 border border-green-400 rounded-lg p-4">
                            <p className="text-green-200">
                              <span className="font-semibold">Success Criteria:</span> {phase.successCriteria}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Timeline Visualization */}
        <div className={`${cardBg} rounded-xl p-8 border mb-12`}>
          <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
            <Calendar className="w-6 h-6" />
            Timeline & Milestones
          </h2>
          
          <div className="relative">
            {/* Week numbers */}
            <div className="flex mb-4">
              {Array.from({ length: 12 }, (_, i) => i + 1).map((week) => (
                <div key={week} className="flex-1 text-center">
                  <div className={`text-sm font-semibold ${muted}`}>W{week}</div>
                </div>
              ))}
            </div>

            {/* Timeline bar */}
            <div className={`relative h-8 rounded-full overflow-hidden mb-8 ${isDark ? 'bg-gray-900' : 'bg-slate-200'}`}>
              {phases.map((phase) => {
                const startWeek = phase.weekNumbers[0];
                const endWeek = phase.weekNumbers[phase.weekNumbers.length - 1];
                const duration = endWeek - startWeek + 1;
                const left = ((startWeek - 1) / 12) * 100;
                const width = (duration / 12) * 100;
                
                return (
                  <div
                    key={phase.id}
                    className={`absolute h-full bg-gradient-to-r ${phase.color} flex items-center justify-center text-white text-xs font-semibold`}
                    style={{ left: `${left}%`, width: `${width}%` }}
                  >
                    {phase.name.split(':')[0]}
                  </div>
                );
              })}
            </div>

            {/* Milestones */}
            <div className="space-y-2">
              {milestones.map((milestone, idx) => {
                const position = ((milestone.week - 0.5) / 12) * 100;
                
                return (
                  <div key={idx} className="flex items-center gap-3">
                    <div className="w-full relative h-2">
                      <div
                        className={`absolute w-3 h-3 rounded-full ${getMilestoneColor(milestone.type)} ${isDark ? 'border-2 border-gray-800' : 'border-2 border-white'}`}
                        style={{ left: `${position}%`, top: '-2px' }}
                      />
                    </div>
                    <div className={`min-w-[250px] rounded px-3 py-1 border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
                      <span className={`text-sm ${isDark ? 'text-white' : 'text-slate-900'}`}>{milestone.title}</span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Legend */}
            <div className="flex gap-4 mt-6 justify-center">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500"></div>
                <span className={`text-sm ${muted}`}>Technical Milestone</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-blue-500"></div>
                <span className={`text-sm ${muted}`}>Deployment</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-purple-500"></div>
                <span className={`text-sm ${muted}`}>Business Milestone</span>
              </div>
            </div>
          </div>
        </div>

        {/* Weekly Breakdown */}
        {showDetails && (
          <div className={`${cardBg} rounded-xl p-8 border mb-12 animate-fadeIn`}>
            <h2 className={`text-2xl font-bold mb-6 flex items-center gap-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              <FileText className="w-6 h-6" />
              Detailed Weekly Breakdown
            </h2>
            <div className="grid md:grid-cols-2 gap-4">
              {weeklyBreakdown.map((week) => (
                <div key={week.week} className={`rounded-lg p-4 border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-slate-50 border-slate-200'}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className={`text-lg font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>Week {week.week}</span>
                    <span className="px-2 py-1 bg-blue-500 bg-opacity-20 text-blue-400 text-xs rounded">
                      {week.hours}h
                    </span>
                  </div>
                  <p className={`${subMuted} text-sm mb-2`}>{week.focus}</p>
                  <span className={`text-xs ${muted}`}>{week.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Key Insights */}
        <div className="grid md:grid-cols-3 gap-6">
          <div className={`${isDark ? 'bg-green-500 bg-opacity-10 border-2 border-green-500' : 'bg-green-50 border-2 border-green-500'} rounded-xl p-6`}>
            <TrendingUp className="w-8 h-8 text-green-400 mb-3" />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Critical Path: 5 Phases</h3>
            <p className={`${muted} text-sm`}>Phases 1, 2, 3, and 5 are on the critical path. Any delay here impacts the final deadline.</p>
          </div>
          
          <div className={`${isDark ? 'bg-blue-500 bg-opacity-10 border-2 border-blue-500' : 'bg-blue-50 border-2 border-blue-500'} rounded-xl p-6`}>
            <Clock className="w-8 h-8 text-blue-400 mb-3" />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>Built-in Buffer: Week 12</h3>
            <p className={`${muted} text-sm`}>Entire final week dedicated to contingency. If everything goes smoothly, use for extra polish.</p>
          </div>
          
          <div className={`${isDark ? 'bg-purple-500 bg-opacity-10 border-2 border-purple-500' : 'bg-purple-50 border-2 border-purple-500'} rounded-xl p-6`}>
            <Zap className="w-8 h-8 text-purple-400 mb-3" />
            <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-slate-900'}`}>AI-Accelerated Build</h3>
            <p className={`${muted} text-sm`}>Using Cursor, v0.dev, and Claude to 3-5x development speed. Solo founder can match small team output.</p>
          </div>
        </div>

        {/* Footer Note */}
        <div className={`mt-12 text-center text-sm ${muted}`}>
          <p>Click any phase to see detailed deliverables, tech stack, and risk mitigation • Toggle weekly breakdown for hour-by-hour planning</p>
        </div>
      </div>
    </div>
  );
};

export default SiloettRoadmap;
